/**
 * Packed livery data (M6 P23, D-252): a look's style guide §5.4 palette (plus the kit wear chip and tonal look, D-341) as 20 vec4 rows (linear rgb + one scalar) that
 * the shared slot materials read per object (context.ts `createPalette`). Each mesh carries its look's array in
 * `userData[LIVERY_KEY]`; swapping the array swaps the livery without a new pipeline.
 */
import { Color } from 'three/webgpu'
import type { FactionPalette, WearLook } from '../data'

export const LIVERY_KEY = 'eclLivery'

/** Row index of each colour; the scalar riding in the row's w is in the comment. */
export const LIVERY_ROW = {
  paintPrimary: 0, // w: wear
  paintSecondary: 1, // w: grimeAmount
  paintAccent: 2, // w: sootAmount
  metalTint: 3, // w: oxidation
  primer: 4, // w: gloss
  decalInk: 5, // w: bleach
  decalInkAlt: 6, // w: pitting
  hazardA: 7, // w: metalRoughness
  hazardB: 8, // w: glassGlowY
  engineCore: 9, // w: emissiveIdleY
  sensor: 10, // w: tonal amount
  grime: 11, // w: tonal desaturate
  glassTint: 12, // w: tonal frequency
  glassGlow: 13,
  emissiveIdle: 14,
  paintRoughness: 15, // xyz: primary, secondary, accent roughness
  chipUnderPrimary: 16, // under-layer below chipped primary paint; w: chip edge gain
  chipUnderSecondary: 17, // w: bare-metal depth
  chipUnderAccent: 18, // w: chip noise frequency multiplier (1 / chips.size)
  chipShape: 19, // x, y: contrast lo, hi on ECL_Mask G; z: 1 = contrast on; w: large-chip weight
} as const

export const LIVERY_ROWS = 20

/** Livery colours that override a base look (the player corporation's `CorpRecord.livery`, `#rrggbb`). */
export interface LiveryColors {
  readonly primary: string
  readonly secondary: string
  readonly accent: string
}

const scratch = new Color()

function writeRow(out: Float32Array, row: number, hex: string, w: number): void {
  scratch.set(hex)
  out[row * 4] = scratch.r
  out[row * 4 + 1] = scratch.g
  out[row * 4 + 2] = scratch.b
  out[row * 4 + 3] = w
}

/**
 * Packs a palette and its wear look (`wearLookOfPalette`) into `out` (LIVERY_ROWS × 4 floats); `colors` replaces the
 * three paint zones. A look without a `chips` block packs the legacy response (primer under every zone, raw G). Not
 * per frame.
 */
export function packLivery(
  p: FactionPalette,
  look: WearLook,
  out: Float32Array = new Float32Array(LIVERY_ROWS * 4),
  colors: LiveryColors | null = null,
): Float32Array {
  const R = LIVERY_ROW
  writeRow(out, R.paintPrimary, colors?.primary ?? p.paintPrimary, p.wear)
  writeRow(out, R.paintSecondary, colors?.secondary ?? p.paintSecondary, p.grimeAmount)
  writeRow(out, R.paintAccent, colors?.accent ?? p.paintAccent, p.sootAmount)
  writeRow(out, R.metalTint, p.metalTint, p.oxidation)
  writeRow(out, R.primer, p.primer, p.gloss)
  writeRow(out, R.decalInk, p.decalInk, p.bleach)
  writeRow(out, R.decalInkAlt, p.decalInkAlt, p.pitting)
  writeRow(out, R.hazardA, p.hazardA, p.metalRoughness)
  writeRow(out, R.hazardB, p.hazardB, p.glassGlowY)
  writeRow(out, R.engineCore, p.engineCore, p.emissiveIdleY)
  writeRow(out, R.sensor, p.sensor, look.tonal.amount)
  writeRow(out, R.grime, p.grime, look.tonal.desaturate)
  writeRow(out, R.glassTint, p.glassTint, look.tonal.frequency)
  writeRow(out, R.glassGlow, p.glassGlow, 0)
  writeRow(out, R.emissiveIdle, p.emissiveIdle, 0)
  const r = R.paintRoughness * 4
  out[r] = p.paintRoughness[0]
  out[r + 1] = p.paintRoughness[1]
  out[r + 2] = p.paintRoughness[2]
  out[r + 3] = 0
  const u = look.under
  writeRow(out, R.chipUnderPrimary, u ? u[0] : p.primer, look.edge)
  writeRow(out, R.chipUnderSecondary, u ? u[1] : p.primer, look.bareDepth)
  writeRow(out, R.chipUnderAccent, u ? u[2] : p.primer, look.chipFreqMul)
  const s = R.chipShape * 4
  out[s] = look.contrast ? look.contrast[0] : 0
  out[s + 1] = look.contrast ? look.contrast[1] : 1
  out[s + 2] = look.contrast ? 1 : 0
  out[s + 3] = look.large
  return out
}
